import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { APPLICATION_FILTER_OPTIONS_QUERY_KEY } from "../../hooks/useApplicationFilterOptions";
import type { CustomInnerHeaderProps } from "ag-grid-react";
import { HugeiconsIcon } from "@hugeicons/react";
import { FilterIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import { getColumnFilterConfig } from "../../utils/columnFilters";
import { useColumnFiltersContext } from "./context";
import { ValuesFilterPanel } from "./ValuesFilterPanel";
import { DateRangeFilterPanel } from "./DateRangeFilterPanel";

const isConfigActive = (
  config: NonNullable<ReturnType<typeof getColumnFilterConfig>>
) =>
  config.kind === "values"
    ? config.selected.length > 0
    : Boolean(config.range?.from || config.range?.to);

// Inner header: AG Grid keeps drawing the sort arrow and handling clicks for sorting; this
// adds the column's filter button next to the label.
export const ColumnFilterHeader = ({
  displayName,
  column
}: CustomInnerHeaderProps) => {
  const context = useColumnFiltersContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [button, setButton] = useState<HTMLButtonElement | null>(null);

  // Options are cached for the table (and server-side for hours); refresh them whenever a
  // filter opens, as the Filters drawer does.
  useEffect(() => {
    if (open)
      queryClient.invalidateQueries({
        queryKey: [APPLICATION_FILTER_OPTIONS_QUERY_KEY]
      });
  }, [open]);

  const config = useMemo(
    () =>
      context
        ? getColumnFilterConfig(column.getColId(), displayName, context)
        : null,
    [context, column, displayName]
  );

  // AG Grid sorts on a native click on the header cell (and on Enter while the header is
  // focused), which runs before React's root listener, so the button must stop both events
  // natively or opening it would also sort the column.
  useEffect(() => {
    if (!button) return;
    const handleClick = (event: MouseEvent) => {
      event.stopPropagation();
      setOpen((prev) => !prev);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    };
    button.addEventListener("click", handleClick);
    button.addEventListener("keydown", handleKeyDown);
    return () => {
      button.removeEventListener("click", handleClick);
      button.removeEventListener("keydown", handleKeyDown);
    };
  }, [button]);

  if (!config || !context)
    return <span className="ag-header-cell-text">{displayName}</span>;

  const isActive = isConfigActive(config);
  const close = () => setOpen(false);

  return (
    <div className="flex min-w-0 items-center gap-1">
      <span
        className={cx("ag-header-cell-text", isActive && "text-primary-600")}
      >
        {displayName}
      </span>
      <Popover open={open} onOpenChange={setOpen} placement="bottom-start">
        <PopoverTrigger asChild>
          <button
            ref={setButton}
            type="button"
            aria-label={`Filter ${displayName}`}
            data-testid={`column-filter-${column.getColId()}`}
            className={cx(
              "relative flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-alpha-40",
              isActive || open ? "text-primary-500" : "text-content-300"
            )}
          >
            <HugeiconsIcon icon={FilterIcon} size={14} strokeWidth={2} />
            <Show if={isActive}>
              <span className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-primary-500" />
            </Show>
          </button>
        </PopoverTrigger>
        <PopoverContent className="z-50 rounded-lg border border-alpha-40 bg-white shadow-lg">
          {config.kind === "values" ? (
            <ValuesFilterPanel
              idPrefix={column.getColId()}
              title={config.title}
              options={config.options}
              selected={config.selected}
              searchable={config.searchable}
              coversEveryApplication={config.coversEveryApplication}
              onCancel={close}
              onApply={(values) => {
                const { apply, applyDirect } = config;
                if (apply) context.setFilters((prev) => apply(prev, values));
                else applyDirect(values);
                close();
              }}
            />
          ) : (
            <DateRangeFilterPanel
              title={config.title}
              dateKey={config.dateKey}
              range={config.range}
              onCancel={close}
              onApply={(range) => {
                context.setFilters((prev) => config.apply(prev, range));
                close();
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
